import React, {useEffect} from "react";

import {IWarehouse} from "../../constants/Warehouses/IWarehouse";
import warehousesService from "../../api/services/warehousesService";
import Input from "../commons/Input/Input";
import classNames from "classnames";
import {IStocksWriteOffWarehouse} from "../../constants/StocksReserve/IStocksWriteOffWarehouse";
import {t} from "i18next";
import {warehouseTypeLabels} from "../Tables/WarehouseTable/parse-warehouse-type";

interface IExportSourceAndReservingWhsSelectorProps {
    sellerID: number
    isDistribute: boolean
    isEnabledAutoReserving: boolean
    isEnableAutoExport: boolean
    isTakeAllWhsAsSource: boolean
    onChangeIsTakeAllWhsAsSource: (data: boolean) => void,
    sourceWhIDs: string[]
    onChangeSourceWhIDs: (data: string[]) => void
    isWriteOffAllWhs: boolean
    onChangeIsWriteOffAllWhs: (data: boolean) => void,
    stocksWriteOffWarehouses: IStocksWriteOffWarehouse[]
    onChangeWriteOffWhs: (data: IStocksWriteOffWarehouse[]) => void
}

const ExportSourceAndReservingWhsSelector: React.FC<IExportSourceAndReservingWhsSelectorProps> = (
    {
        sellerID,
        isDistribute,
        isEnabledAutoReserving,
        isEnableAutoExport,
        isTakeAllWhsAsSource,
        onChangeIsTakeAllWhsAsSource,
        sourceWhIDs,
        onChangeSourceWhIDs,
        isWriteOffAllWhs,
        onChangeIsWriteOffAllWhs,
        stocksWriteOffWarehouses,
        onChangeWriteOffWhs
    }
) => {

    const [initialSourceWhs, setInitialSourceWhs] = React.useState<string[]>([]);
    const [initialWriteOffWhs, setInitialWriteOffWhs] = React.useState<IStocksWriteOffWarehouse[]>([]);
    const [warehouses, setWarehouses] = React.useState<IWarehouse[]>([]);

    useEffect(() => {
        // --- Prepare whs ---
        warehousesService.getWarehousesForSeller(sellerID).then(res => {
            const whs = (res?.data?.data || []).filter((wh: IWarehouse) => wh.status === 'active' && wh.isForSale)
            setWarehouses(whs)
        })
    }, [])

    useEffect(() => {
        if (warehouses.length === 0) {
            return
        }

        // --- SourceWHs ---
        onInitIsTakeAllWhsAsSource()

        // --- WriteOFF ---
        onInitIsWriteOffAllWhs()
    }, [warehouses])


    // --- SourceWHs ---
    useEffect(() => {
        if (warehouses.length > 0) {
            isTakeAllWhsAsSource = isDistribute
            onToggleIsTakeAllWhsAsSource()
            onChangeIsTakeAllWhsAsSource(isTakeAllWhsAsSource)
        }
    }, [isDistribute])

    useEffect(() => {
        const isNotSelectedAnyWH = sourceWhIDs.length === 0
        const isNotWasSelectedAnyWH = initialSourceWhs.length === 0

        if ((isNotSelectedAnyWH || isNotWasSelectedAnyWH) && warehouses.length > 0) {
            isTakeAllWhsAsSource = isEnableAutoExport
            onToggleIsTakeAllWhsAsSource()
            onChangeIsTakeAllWhsAsSource(isTakeAllWhsAsSource)
        }
    }, [isEnableAutoExport])

    const isTakeAllWhsAsSourceChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {

        isTakeAllWhsAsSource = !isTakeAllWhsAsSource
        onToggleIsTakeAllWhsAsSource()
        onChangeIsTakeAllWhsAsSource(isTakeAllWhsAsSource)
    }

    const onSelectSourceWh = (selectedWhUUID: string) => {
        if (sourceWhIDs.includes(selectedWhUUID)) {
            sourceWhIDs = sourceWhIDs.filter(whUUID => whUUID !== selectedWhUUID)
        } else {
            sourceWhIDs.push(selectedWhUUID)
        }

        onChangeSourceWhIDs(sourceWhIDs)
    }

    const onInitIsTakeAllWhsAsSource = () => {
        if (isDistribute) {
            isTakeAllWhsAsSource = true
            onChangeIsTakeAllWhsAsSource(isTakeAllWhsAsSource)
        }

        if (isTakeAllWhsAsSource) {
            sourceWhIDs = warehouses.map(wh => wh.uuid || '')
        }

        if(initialSourceWhs.length == 0) {
            setInitialSourceWhs([...sourceWhIDs])
        }

        const isNotSelectedAnyWH = sourceWhIDs.length === 0
        if (isEnableAutoExport && isNotSelectedAnyWH) {
            isTakeAllWhsAsSource = true
            onChangeIsTakeAllWhsAsSource(isTakeAllWhsAsSource)
            sourceWhIDs = warehouses.map(wh => wh.uuid || '')
        }

        onChangeSourceWhIDs(sourceWhIDs)
    }

    const onToggleIsTakeAllWhsAsSource = () => {
        if (isTakeAllWhsAsSource) {
            sourceWhIDs = warehouses.map(wh => wh.uuid || '')
        } else {
            sourceWhIDs = [...initialSourceWhs]
        }

        onChangeSourceWhIDs(sourceWhIDs)
    }

    // --- WriteOFF ---
    useEffect(() => {
        const isNotSelectedAnyWH = stocksWriteOffWarehouses.length === 0
        const isNotWasSelectedAnyWH = initialWriteOffWhs.length === 0

        if ((isNotSelectedAnyWH || isNotWasSelectedAnyWH) && warehouses.length > 0) {
            isWriteOffAllWhs = isEnabledAutoReserving
            onToggleIsWriteOffAllWhs()
            onChangeIsWriteOffAllWhs(isWriteOffAllWhs)
        }
    }, [isEnabledAutoReserving])

    const isWriteOffAllWhsChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        isWriteOffAllWhs = !isWriteOffAllWhs
        onToggleIsWriteOffAllWhs()
        onChangeIsWriteOffAllWhs(isWriteOffAllWhs)
    }

    const onSelectWriteOffWh = (selectedWhUUID: string) => {
        if (stocksWriteOffWarehouses.filter(r => r.warehouseID === selectedWhUUID).length > 0) {
            stocksWriteOffWarehouses = stocksWriteOffWarehouses.filter(r => r.warehouseID !== selectedWhUUID)
        } else {
            const wh = warehouses.filter(wh => wh.uuid === selectedWhUUID)[0] || {}
            const maxPriority = stocksWriteOffWarehouses.length > 0 ? Math.max(...stocksWriteOffWarehouses.map((w) => w.priority)) : 0
            let nextPriority = maxPriority + 1
            stocksWriteOffWarehouses.push({warehouse: wh, "warehouseID": selectedWhUUID, priority: nextPriority})
        }

        onChangeWriteOffWhs(stocksWriteOffWarehouses)
    }

    const onInitIsWriteOffAllWhs = () => {
        if (isWriteOffAllWhs) {
            markAllWhsAsWriteOff()
        }

        if (initialWriteOffWhs.length == 0) {
            setInitialWriteOffWhs([...stocksWriteOffWarehouses])
        }

        const isNotSelectedAnyWH = stocksWriteOffWarehouses.length === 0
        if (isEnabledAutoReserving && isNotSelectedAnyWH) {
            isWriteOffAllWhs = true
            onChangeIsWriteOffAllWhs(isWriteOffAllWhs)
            markAllWhsAsWriteOff()
        }

        onChangeWriteOffWhs(stocksWriteOffWarehouses)
    }

    const onToggleIsWriteOffAllWhs = () => {
        if (isWriteOffAllWhs) {
            markAllWhsAsWriteOff()
        } else {
            stocksWriteOffWarehouses = [...initialWriteOffWhs]
        }

        onChangeWriteOffWhs(stocksWriteOffWarehouses)
    }

    const markAllWhsAsWriteOff = () => {
        const maxPriority = stocksWriteOffWarehouses.length > 0 ? Math.max(...stocksWriteOffWarehouses.map((w) => w.priority)) : 0
        let nextPriority = maxPriority + 1
        warehouses.forEach((wh, i) => {
            const whUUID = wh.uuid || ''
            if (stocksWriteOffWarehouses.filter(r => r.warehouseID === whUUID).length === 0) {
                stocksWriteOffWarehouses.push({warehouse: wh, "warehouseID": whUUID, priority: nextPriority})
                nextPriority++
            }
        })
    }


    return (
        <>
            <table className="table-auto w-full block border p-2 rounded schedule-table text-[#64748B]">
                <thead className="text-xs uppercase text-gray-400">
                <tr className="">
                    <th className="w-[57%] pr-[10px] align-vertical">
                        <div className="font-semibold text-left">Источники</div>
                    </th>
                    <th className={`w-[15%] align-vertical`}>
                        <div className="font-semibold text-left">Склад сборки</div>
                    </th>
                    <th className={`w-[13%] pr-[30px] align-vertical`}>
                        <div className="font-semibold text-right">Приоритет сборки</div>
                    </th>
                    <th className="w-[15%] pr-3 align-vertical">
                        <div className="font-semibold text-right">Источник остатков, Σ</div>
                    </th>
                </tr>
                </thead>
                <tbody className="text-sm">
                    <tr className={classNames('border-gray-200', {'border-b': warehouses.length})}>
                        <td className={`py-2 font-medium text-c-light-black`}>
                            Все
                        </td>
                        <td >
                            <input
                                onChange={isWriteOffAllWhsChangeHandler}
                                className='rounded'
                                type="checkbox"
                                name={'wo'}
                                checked={isWriteOffAllWhs}
                            />
                        </td>
                        <td></td>
                        <td className="text-right pr-3">
                            <input
                                onChange={isTakeAllWhsAsSourceChangeHandler}
                                className={classNames('rounded', {'text-[#94A3B8]': isDistribute})}
                                type="checkbox"
                                name={'ss'}
                                checked={isTakeAllWhsAsSource}
                                disabled={isDistribute}
                            />
                        </td>
                    </tr>
                    {warehouses.map((wh, key) => {
                        const whUUID = wh.uuid || ''
                        const writeOffWh = stocksWriteOffWarehouses.filter(r => r.warehouseID === whUUID)[0] || null

                        return <tr className={classNames('border-gray-200', {'border-b': key + 1 < warehouses.length})} key={key}>
                            <td className={`py-1`}>
                                {wh.name}
                                <div className={`text-[12px] flex flex-row text-gray-400`}>Мой</div>
                            </td>
                            <td>
                                <input
                                    onChange={() => onSelectWriteOffWh(whUUID)}
                                    className={classNames('rounded', {'text-[#94A3B8]': isWriteOffAllWhs})}
                                    type="checkbox"
                                    name={wh.name + '_wo'}
                                    checked={writeOffWh !== null}
                                />
                            </td>
                            <td className="text-right pr-[30px]">
                                <Input
                                    placeholder={''}
                                    id={wh.name + '_wo_pri'}
                                    name={wh.name + '_wo_pri'}
                                    value={(writeOffWh?.priority || '').toString()}
                                    min={1}
                                    max={warehouses.length}
                                    label={false}
                                    type="number"
                                    changeHandler={(e) => writeOffWh['priority'] = parseInt(e.target.value)}
                                    customClassName={classNames("text-[14px] pr-0 text-right text-gray-600")}
                                    customLabelClassName={"text-sm"}
                                    validationError={false}
                                    validationErrorDescription={''}
                                    disabled={writeOffWh === null}
                                />
                            </td>
                            <td className="text-right pr-3">
                                <input
                                    onChange={() => onSelectSourceWh(whUUID)}
                                    className={classNames('rounded', {'text-[#94A3B8]': isTakeAllWhsAsSource})}
                                    type="checkbox"
                                    name={wh.name + '_ss'}
                                    checked={sourceWhIDs.includes(whUUID)}
                                    disabled={isTakeAllWhsAsSource}
                                />
                            </td>
                        </tr>
                    })}
                </tbody>
            </table>
            {warehouses.length === 0 &&
                <div>Для выполнениянастройки, в разделе Склады включите флаг "К продаже" минимум у одного склада</div>
            }
        </>
    )

}

export default ExportSourceAndReservingWhsSelector;